import { EmployeesSection } from "@/components/employee/EmployeesSection";
import { Employee } from "../hooks/useEmployeeManagement";
import { User } from "@/types";

interface EmployeeTabsProps {
  onViewProfile: (employee: Employee) => void;
  onEdit: (employee: Employee) => void;
  onViewOvertimeHistory: (employee: Employee) => void;
  onChangeDepartment: (employee: Employee) => void;
  onChangeStatus: (employee: Employee) => void;
  isNewUserDialogOpen: boolean;
  onNewUserOpenChange: (open: boolean) => void;
  departments?: Array<{ id: string; name: string; company_id: string }>;
  isLoadingDepartments?: boolean;
  acceptedDomains?: string[];
  createNewUser: (employeeData: { name: string; email?: string; department: string; position?: string; residency?: string; workerType?: string; employee_id?: string }, shiftAssignment?: { shiftId: string; fromDate: string; toDate?: string }) => Promise<void>;
  // ✅ NEW: Add centralized state props
  employees: Employee[];
  isLoadingEmployees: boolean;
  employeesError: string | null;
}

export function EmployeeTabs({
  onViewProfile,
  onEdit,
  onViewOvertimeHistory,
  onChangeDepartment,
  onChangeStatus,
  isNewUserDialogOpen,
  onNewUserOpenChange,
  departments = [],
  isLoadingDepartments = false,
  acceptedDomains = [],
  createNewUser,
  // ✅ NEW: Use centralized state props
  employees,
  isLoadingEmployees,
  employeesError
}: EmployeeTabsProps) {
  // Adapter function to convert between different parameter signatures
  const handleDepartmentChange = (employeeId: string) => {
    // Find the employee by ID
    const employee = { id: employeeId } as Employee; // We only need the ID for this case
    onChangeDepartment(employee);
  };

  // Adapter function to convert the old addEmployee interface to the new createNewUser interface
  const addEmployeeAdapter = async (employeeData: Partial<Omit<User, 'id'>> & { email: string; name: string; role: string }, shiftAssignment?: { shiftId: string; fromDate: string; toDate?: string }) => {
    // Convert the old interface to the new one
    const newEmployeeData = {
      name: employeeData.name,
      email: employeeData.email,
      department: employeeData.department || '',
      position: employeeData.position || '',
      residency: employeeData.residency || '',
      workerType: employeeData.workerType || '',
      employee_id: (employeeData as { employee_id?: string }).employee_id || ''
    };
    
    // Call the new function with shift assignment
    await createNewUser(newEmployeeData, shiftAssignment);
  };

  return (
    <EmployeesSection
      onViewProfile={onViewProfile}
      onEdit={onEdit}
      onViewOvertimeHistory={onViewOvertimeHistory}
      onChangeDepartmentProp={handleDepartmentChange}
      onChangeStatus={onChangeStatus}
      canCreateUsers={true}
      isNewUserDialogOpen={isNewUserDialogOpen}
      setIsNewUserDialogOpen={onNewUserOpenChange}
      addEmployee={addEmployeeAdapter}
      departments={departments}
      isLoadingDepartments={isLoadingDepartments}
      acceptedDomains={acceptedDomains}
      // ✅ NEW: Pass centralized state
      employees={employees}
      isLoadingEmployees={isLoadingEmployees}
      employeesError={employeesError}
    />
  );
}
